import React from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';

import { Typography } from '../../../components/atoms';
import { InlineBulletList } from '../../../components/molecules';
import * as Api from '../../../weborder/Api';
import {
  getLocationDetails,
  getRestaurantNotes,
} from '../utils/locationHeaderUtils';

type Props = {
  currency: Api.Currency;
  deliveryLocation: Api.DeliveryLocation | null;
  languageCode: string;
  restaurantData: Api.CollectionRestaurantDetail | Api.DeliveryRestaurantDetail;
  translate: any;
};

const useStyles = makeStyles((theme: Theme) => ({
  locationHeader: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(0, 2),
  },

  details: {
    alignItems: 'flex-start',
    display: 'flex',
    flexFlow: 'column nowrap',

    '& svg': {
      left: 0,
      position: 'absolute',
      top: 'calc(50% - 15px)',
    },

    '& .fd-text': {
      lineHeight: theme.typography.pxToRem(16),
    },

    '& .fd-text__body2:first-of-type': {
      paddingRight: theme.spacing(0.5),
    },
  },

  orderType: {
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(1),
    position: 'relative',
  },

  [theme.breakpoints.up('sm')]: {
    locationHeader: {
      padding: theme.spacing(0, 3),
    },
  },

  [theme.breakpoints.up('md')]: {
    locationHeader: {
      padding: 0,
    },

    details: {
      flexDirection: 'row',
      marginBottom: theme.spacing(1),

      '& .title': {
        marginRight: theme.spacing(1),
      },
    },

    orderType: {
      marginBottom: 0,
    },
  },
}));

export function LocationHeader(props: Props): JSX.Element | null {
  const {
    currency,
    deliveryLocation,
    languageCode,
    restaurantData,
    translate,
  } = props;

  const classes = useStyles();

  const locationDetails = getLocationDetails(
    restaurantData,
    deliveryLocation,
    classes,
    'body2',
    translate,
  );

  const restaurantNotes = getRestaurantNotes(
    restaurantData,
    currency,
    languageCode,
    translate,
  );

  const locationHeaderStyles = clsx(
    classes.locationHeader,
    'fd-section__location-details',
  );

  return (
    <div className={locationHeaderStyles}>
      <div className={classes.details}>
        <div className={classes.orderType}>
          {locationDetails.icon}

          <Typography>
            <em>{locationDetails.title}</em>
          </Typography>
        </div>

        {locationDetails.address || ''}
      </div>

      {!!restaurantNotes.length && <InlineBulletList notes={restaurantNotes} />}
    </div>
  );
}
